import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SharedHeader from './SharedHeader';
import CreateAccountModal from './CreateAccountModal';
import { useAuth } from '../contexts/AuthContext';
import { INITIAL_BUILDER, QUICK_FILTERS, GRADE_FILTERS, SORT_OPTIONS, searchSelections,
  compileSearch, searchSummary, trackSearchExecution, builderFromSelections,
  defaultSearchName, listSavedSearches, saveSearch, updateSavedSearch,
  deleteSavedSearch, randomTypoExamples, TYPO_LISTING_TYPES } from '../utils/coinSearch';
import './QuickFlipSearch.css';

const TOOL_HEADLINES = {
  typo: 'Find the listings everyone else misses.',
  builder: "Level up your eBay searches. Save time and see more of what you want and less of what you don't."
};

const SEARCH_DRAFT_KEY = 'coin-search-draft-v1';

const readSearchDraft = () => {
  try {
    const draft = JSON.parse(window.sessionStorage.getItem(SEARCH_DRAFT_KEY));
    if (!draft || !['typo', 'builder'].includes(draft.tool)) return null;
    return {
      tool: draft.tool,
      player: typeof draft.player === 'string' ? draft.player : '',
      removedVariants: Array.isArray(draft.removedVariants)
        ? draft.removedVariants.filter(term => typeof term === 'string') : [],
      typoFormat: ['bin', 'auction'].includes(draft.typoFormat) ? draft.typoFormat : '',
      builder: {
        ...INITIAL_BUILDER,
        ...(draft.builder && typeof draft.builder === 'object' ? draft.builder : {}),
        filters: Array.isArray(draft.builder?.filters) ? draft.builder.filters : []
      }
    };
  } catch (_) {
    return null;
  }
};

const writeSearchDraft = (tool, player, builder, removedVariants, typoFormat) => {
  try {
    window.sessionStorage.setItem(SEARCH_DRAFT_KEY, JSON.stringify({ tool, player, builder, removedVariants, typoFormat }));
  } catch (_) { /* Search remains usable if browser storage is unavailable. */ }
};

function QuickFlipSearch({ requestDelay = 1500 }) {
  const { isAuthenticated } = useAuth();
  const initialDraft = useMemo(readSearchDraft, []);
  const [tool, setTool] = useState(initialDraft?.tool || 'builder');
  const [player, setPlayer] = useState(initialDraft?.player || '');
  const [builder, setBuilder] = useState(initialDraft?.builder || INITIAL_BUILDER);
  // Typo Hunter suggestions the user struck out, plus every suggestion seen for
  // the current keyword so removed terms stay visible (and restorable) after
  // the rebuilt search stops returning them.
  const [removedVariants, setRemovedVariants] = useState(initialDraft?.removedVariants || []);
  const [typoFormat, setTypoFormat] = useState(initialDraft?.typoFormat || '');
  const [suggestionPool, setSuggestionPool] = useState({ player: '', variants: [] });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef(null);
  const actionsRef = useRef(null);
  const [savedSearches, setSavedSearches] = useState([]);
  const [savedNotice, setSavedNotice] = useState('');
  const [savedError, setSavedError] = useState('');
  const [editingSearch, setEditingSearch] = useState(null);
  const [rerunningId, setRerunningId] = useState(null);
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  // Set when Save Search was what sent a signed-out user to the modal, so the
  // save they asked for completes as soon as the account exists.
  const saveAfterSignup = useRef(false);
  const noticeTimer = useRef(null);
  const [actionsHeight, setActionsHeight] = useState(220);
  const selections = useMemo(
    () => searchSelections(tool, player, builder, removedVariants, typoFormat),
    [tool, player, builder, removedVariants, typoFormat]
  );
  const hasSearchInput = tool === 'typo'
    ? Boolean(player.trim())
    : Boolean(
      builder.players.trim() || builder.sets.trim() || builder.include.trim() || builder.exclude.trim() ||
      builder.priceMin || builder.priceMax || builder.format || builder.sold ||
      builder.sort !== INITIAL_BUILDER.sort || builder.filters.length
    );
  // Sampled once per mount so a page reload shows a different trio.
  const typoExamples = useMemo(() => randomTypoExamples(), []);
  const stateKey = JSON.stringify(selections);
  // Immediately invalidate an old link on edit, including the render before
  // the effect cleanup runs. Aborted responses can never restore that link.
  const currentResult = result?.stateKey === stateKey ? result : null;
  const ebayUrl = currentResult?.url || '';
  const typoKeyword = player.trim();
  const typoSuggestions = tool === 'typo' && suggestionPool.player === typoKeyword
    ? [...new Set([...suggestionPool.variants, ...removedVariants])]
    : removedVariants;

  useEffect(() => () => {
    window.clearTimeout(copyTimer.current);
    window.clearTimeout(noticeTimer.current);
  }, []);
  useEffect(() => {
    writeSearchDraft(tool, player, builder, removedVariants, typoFormat);
  }, [tool, player, builder, removedVariants, typoFormat]);
  useEffect(() => {
    if (currentResult?.selections?.mode !== 'typo') return;
    const keyword = currentResult.selections.players[0];
    setSuggestionPool(pool => ({
      player: keyword,
      variants: [...new Set([...(pool.player === keyword ? pool.variants : []), ...(currentResult.variants || [])])]
    }));
  }, [currentResult]);
  const refreshSaved = useCallback(async () => {
    try {
      const searches = await listSavedSearches();
      setSavedSearches(searches || []);
    } catch (err) {
      setSavedError(err.message || 'Unable to load saved searches.');
    }
  }, []);
  useEffect(() => {
    if (!isAuthenticated) { setSavedSearches([]); return; }
    refreshSaved();
  }, [isAuthenticated, refreshSaved]);
  useEffect(() => {
    if (tool !== 'builder' || !window.ResizeObserver) return undefined;
    const observer = new ResizeObserver(([entry]) => setActionsHeight(entry.target.offsetHeight));
    observer.observe(actionsRef.current);
    return () => observer.disconnect();
  }, [tool]);
  useEffect(() => {
    setResult(null);
    setError('');
    setCopied(false);
    if (!selections.players.length) {
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const data = await compileSearch(selections, controller.signal);
        if (!controller.signal.aborted) setResult({ ...data, stateKey });
      } catch (err) {
        if (!controller.signal.aborted) setError(err.message || 'Unable to build this search.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, requestDelay);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [selections, stateKey, requestDelay]);

  const changePlayer = value => {
    if (value.trim() !== typoKeyword) setRemovedVariants([]);
    setPlayer(value);
  };
  const toggleVariant = variant => setRemovedVariants(current => (current.includes(variant)
    ? current.filter(term => term !== variant) : [...current, variant]));
  const update = (field, value) => setBuilder(current => ({ ...current, [field]: value }));
  const toggle = filter => setBuilder(current => {
    if (current.filters.includes(filter)) {
      return { ...current, filters: current.filters.filter(value => value !== filter) };
    }
    const isIncompatible = value => GRADE_FILTERS.includes(filter) && GRADE_FILTERS.includes(value);
    return {
      ...current,
      filters: [...current.filters.filter(value => !isIncompatible(value)), filter]
    };
  });
  const resetSearch = () => {
    setPlayer('');
    setRemovedVariants([]);
    setTypoFormat('');
    setBuilder({ ...INITIAL_BUILDER });
    setResult(null);
    setError('');
    setCopied(false);
    setEditingSearch(null);
    setSavedNotice('');
    setSavedError('');
  };
  const copyLink = async () => {
    if (!ebayUrl) return;
    try {
      await navigator.clipboard.writeText(ebayUrl);
      setCopied(true);
      window.clearTimeout(copyTimer.current);
      copyTimer.current = window.setTimeout(() => setCopied(false), 1600);
    } catch (_) { setError('Copy failed. Try Copy eBay Link again.'); }
  };
  const showSavedNotice = message => {
    setSavedNotice(message);
    window.clearTimeout(noticeTimer.current);
    noticeTimer.current = window.setTimeout(() => setSavedNotice(''), 2400);
  };
  const persistSearch = async ({ createNew = false, suggestedName, promptLabel = 'Name this search' } = {}) => {
    const suggested = suggestedName || defaultSearchName(selections);
    const name = window.prompt(promptLabel, suggested);
    if (name === null) return;
    setSavedError('');
    try {
      const record = await saveSearch(name.trim() || suggested, selections, { createNew });
      setSavedSearches(current => [record, ...current.filter(item => item.id !== record.id)]);
      setEditingSearch(null);
      showSavedNotice(`Saved "${record.name}"`);
      // Re-read after the write lands so an in-flight list request from the
      // sign-in that preceded this save cannot leave a stale list behind.
      refreshSaved();
    } catch (err) {
      setSavedError(err.message || 'Unable to save this search.');
    }
  };
  const handleSaveClick = () => {
    if (!selections.players.length) return;
    if (!isAuthenticated) {
      saveAfterSignup.current = true;
      setAccountModalOpen(true);
      return;
    }
    persistSearch();
  };
  const handleAccountCreated = () => {
    setAccountModalOpen(false);
    const shouldSave = saveAfterSignup.current;
    saveAfterSignup.current = false;
    if (shouldSave) persistSearch(); else refreshSaved();
  };
  const editSavedSearch = record => {
    const stored = record.selections || {};
    setSavedError('');
    setSavedNotice('');
    setEditingSearch({ id: record.id, name: record.name });
    if (stored.mode === 'typo') {
      setTool('typo');
      setPlayer((stored.players || [])[0] || '');
      setRemovedVariants(Array.isArray(stored.removedVariants) ? stored.removedVariants : []);
      setTypoFormat(['bin', 'auction'].includes(stored.format) ? stored.format : '');
    } else {
      setTool('builder');
      setPlayer('');
      setRemovedVariants([]);
      setBuilder({ ...INITIAL_BUILDER, ...builderFromSelections(stored) });
    }
  };
  const updateEditingSearch = async () => {
    if (!editingSearch || !selections.players.length) return;
    setSavedError('');
    try {
      const record = await updateSavedSearch(editingSearch.id, editingSearch.name, selections);
      setSavedSearches(current => [record, ...current.filter(item => item.id !== record.id)]);
      setEditingSearch(null);
      showSavedNotice(`Updated "${record.name}"`);
      refreshSaved();
    } catch (err) {
      setSavedError(err.message || 'Unable to update this search.');
    }
  };
  const runSavedSearch = async record => {
    const stored = record.selections || {};
    setSavedError('');
    const popup = window.open('about:blank', '_blank');
    if (!popup) {
      setSavedError('Your browser blocked the eBay window. Allow pop-ups and try Re-run again.');
      return;
    }
    popup.opener = null;
    // Placeholder content so the tab shows something while the search link is
    // rebuilt on the server -- otherwise the popup sits on a blank page for
    // the duration of the /api/search/build round trip.
    popup.document.write(
      '<!doctype html><title>Opening eBay…</title>' +
      '<body style="font:15px system-ui,sans-serif;color:#444;padding:2rem;text-align:center">' +
      'Opening your eBay search…</body>'
    );
    setRerunningId(record.id);
    try {
      const compiled = await compileSearch(stored);
      trackSearchExecution(compiled);
      popup.location.replace(compiled.url);
    } catch (err) {
      popup.close();
      setSavedError(err.message || 'Unable to re-run this search.');
    } finally {
      setRerunningId(null);
    }
  };
  const removeSavedSearch = async id => {
    setSavedError('');
    try {
      await deleteSavedSearch(id);
      setSavedSearches(current => current.filter(item => item.id !== id));
      if (editingSearch?.id === id) setEditingSearch(null);
    } catch (err) {
      setSavedError(err.message || 'Unable to delete this search.');
    }
  };
  const textField = (key, label, placeholder) => (
    <label className="qf-field" key={key}>
      <span>{label}</span>
      <input type="text" value={builder[key]} placeholder={placeholder}
        onChange={event => update(key, event.target.value)} autoComplete="off" />
    </label>
  );
  const searchActions = (
    <section className={`qf-actions${tool === 'typo' ? ' qf-actions-inline' : ''}${hasSearchInput ? '' : ' qf-actions--mobile-hidden'}`} aria-label="Search actions" ref={actionsRef}>
      <div className="qf-actions-inner">
        {tool === 'typo' && <p className="qf-link-cue" role="status">
          {loading ? 'Building your search…' : ebayUrl ? 'Your search is ready!'
            : error ? 'Check your input and try again.' : 'Enter name/keyword and we’ll build your search'}
        </p>}
        {editingSearch && <div className="qf-editing-notice" role="status">
          <span>Editing “{editingSearch.name}”</span>
          <button type="button" onClick={() => setEditingSearch(null)}>Cancel edit</button>
        </div>}
        <div className={`qf-action-buttons${editingSearch ? ' is-editing' : ''}`}>
          <a className={`qf-ebay-button${!ebayUrl ? ' is-disabled' : ''}`} href={ebayUrl || undefined}
            target="_blank" rel="noopener noreferrer sponsored" aria-disabled={!ebayUrl}
            aria-describedby="qf-affiliate-disclosure"
            onClick={ebayUrl ? () => trackSearchExecution(currentResult) : undefined}>
            {loading && tool === 'builder' ? 'Building your search…' : 'Search on eBay'} <span aria-hidden="true">↗</span>
          </a>
          <button className="qf-copy-button" type="button" disabled={!ebayUrl} onClick={copyLink}
            aria-describedby="qf-affiliate-disclosure">
            {copied ? 'Copied eBay Link' : 'Copy eBay Link'}
          </button>
          {editingSearch ? <>
            <button className="qf-copy-button qf-save-button" type="button"
              disabled={!selections.players.length} onClick={updateEditingSearch}>
              Update Saved Search
            </button>
            <button className="qf-copy-button qf-save-button" type="button"
              disabled={!selections.players.length} onClick={() => persistSearch({
                createNew: true,
                suggestedName: `${editingSearch.name} copy`,
                promptLabel: 'Name this new search'
              })}>
              Save as New
            </button>
          </> : (
            <button className="qf-copy-button qf-save-button" type="button"
              disabled={!selections.players.length} onClick={handleSaveClick}>
              Save Search
            </button>
          )}
        </div>
        {savedNotice && <p className="qf-save-notice" role="status">{savedNotice}</p>}
        {savedError && <p className="qf-error" role="alert">{savedError}</p>}
        <p className="qf-disclosure" id="qf-affiliate-disclosure">Disclosure: As an eBay Partner, COIN may earn a commission if you make a purchase, at no extra cost to you.</p>
        <p className="qf-disclosure qf-terms-notice">By using COIN Search, you agree to our <Link to="/terms">Terms &amp; Conditions</Link>.</p>
      </div>
    </section>
  );
  return (
    <div className="quick-flip-page">
      <SharedHeader variant="quick-flip" onBeforeLogin={() => writeSearchDraft(tool, player, builder, removedVariants, typoFormat)}
        onCreateAccount={() => {
          saveAfterSignup.current = false;
          setAccountModalOpen(true);
        }} />
      <main className="qf-shell" style={{ paddingBottom: tool === 'builder' ? actionsHeight + 32 : 42 }}>
        <header className="qf-intro">
          <p className="qf-kicker">COIN Search</p>
          <h1>{TOOL_HEADLINES[tool]}</h1>
        </header>
        <div className="qf-workspace">
          <section className="qf-builder-card" aria-label="Search builder">
            <div className="qf-tabs" role="tablist" aria-label="Search tool">
              { [['builder', 'Advanced Search Builder'], ['typo', 'Typo Hunter']].map(([value, label]) => (
                <button key={value} id={`qf-${value}-tab`} type="button" role="tab"
                  aria-selected={tool === value} aria-controls={`qf-${value}-panel`}
                  className={tool === value ? 'is-active' : ''} onClick={() => setTool(value)}>{label}</button>
              )) }
            </div>
            <div className="qf-form-section" role="tabpanel" id={`qf-${tool}-panel`} aria-labelledby={`qf-${tool}-tab`}>
              <div className="qf-form-actions">
                <button className="qf-reset-button" type="button" disabled={!hasSearchInput} onClick={resetSearch}>
                  Reset Search
                </button>
              </div>
              {tool === 'typo' ? (<>
                <label className="qf-field">
                  <span>Enter Player / Name / Keyword</span>
                  <input type="text" value={player} onChange={event => changePlayer(event.target.value)}
                    placeholder="Wembanyama" aria-describedby="qf-keyword-examples" autoComplete="off" />
                </label>
                <p className="qf-input-hint" id="qf-keyword-examples">
                  Try {typoExamples[0]}, {typoExamples[1]}, or {typoExamples[2]}.
                </p>
                <fieldset className="qf-choice-group qf-listing-type">
                  <legend>Listing Type</legend>
                  <div className="qf-choice-grid">
                    {TYPO_LISTING_TYPES.map(([value, label]) => (
                      <label key={value || 'any'} className={typoFormat === value ? 'is-selected' : ''}>
                        <input type="radio" name="qf-typo-listing-type" value={value}
                          checked={typoFormat === value} onChange={() => setTypoFormat(value)} />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                {typoSuggestions.length > 0 && <details key={typoKeyword} className="qf-variants qf-suggestions">
                  <summary>
                    Review suggested searches ({typoSuggestions.length - removedVariants.length} of {typoSuggestions.length})
                  </summary>
                  <small>Remove any suggestion you don't want before searching eBay. Tap a removed term to add it back.</small>
                  <div>{typoSuggestions.map(variant => {
                    const removed = removedVariants.includes(variant);
                    return (
                      <button key={variant} type="button" className={`qf-suggestion${removed ? ' is-removed' : ''}`}
                        aria-label={`${removed ? 'Add back' : 'Remove'} ${variant}`} onClick={() => toggleVariant(variant)}>
                        <span>{variant}</span><span aria-hidden="true">{removed ? '+' : '×'}</span>
                      </button>
                    );
                  })}</div>
                </details>}
                {searchActions}
              </>) : <>
                <p className="qf-input-hint qf-builder-hint">
                  Separate multiple terms with commas. We'll search for any included term and remove every excluded term.
                </p>
                <div className="qf-field-grid">
                  {textField('players', 'Player(s)', 'Wembanyama, Charizard, Shohei Ohtani')}
                  {textField('sets', 'Product(s) / Set(s)', 'Chrome, Prizm, Bowman')}
                  {textField('include', 'Include Keywords', 'Gold, Sapphire, Downtown')}
                  {textField('exclude', 'Exclude Keywords', 'reprint, custom')}
                </div>
                <fieldset className="qf-choice-group">
                  <legend>Quick Filters</legend>
                  <div className="qf-choice-grid">
                    {QUICK_FILTERS.map(([value, label]) => (
                      <label key={value} className={builder.filters.includes(value) ? 'is-selected' : ''}>
                        <input type="checkbox" checked={builder.filters.includes(value)} onChange={() => toggle(value)} />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                <details className="qf-more-options">
                  <summary>More eBay Options</summary>
                  <div className="qf-field-grid">
                    <label className="qf-field"><span>Listing Type</span>
                      <select aria-label="Listing Type" value={builder.format} onChange={event => update('format', event.target.value)}>
                        <option value="">Any</option><option value="bin">Buy It Now</option><option value="auction">Auction</option>
                      </select>
                    </label>
                    <label className="qf-field"><span>Sort</span>
                      <select aria-label="Sort" value={builder.sort} onChange={event => update('sort', event.target.value)}>
                        {SORT_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                      </select>
                    </label>
                    { [['priceMin', 'Minimum Price'], ['priceMax', 'Maximum Price']].map(([key, label]) => (
                      <label className="qf-field" key={key}><span>{label}</span>
                        <div className="qf-price-input">
                          <input type="number" min="0" step="0.01" inputMode="decimal" value={builder[key]}
                            onChange={event => update(key, event.target.value)} />
                        </div>
                      </label>
                    )) }
                  </div>
                  <label className="qf-inline-check">
                    <input type="checkbox" checked={builder.sold} onChange={event => update('sold', event.target.checked)} />
                    <span>Sold Listings</span>
                  </label>
                </details>
              </>}
              {tool === 'builder' && currentResult?.variants?.length > 0 && <details key={stateKey} className="qf-variants">
                <summary>View search variations ({currentResult.variants.length})</summary>
                <div>{currentResult.variants.map(variant => <code key={variant}>{variant}</code>)}</div>
              </details>}
            </div>
          </section>
          <div className="qf-sidebar">
            <aside className="qf-preview-card" aria-live="polite">
              <div className="qf-preview-heading"><h2>Your search</h2>
                <span className="qf-status">{loading ? 'Building' : currentResult ? 'Ready' : 'Waiting'}</span>
              </div>
              <div className="qf-summary">{searchSummary(selections).map((line, i) => <p key={i}>{line}</p>)}</div>
              {currentResult?.warnings?.map(warning => <p className="qf-warning" key={warning}>{warning}</p>)}
              {error && <p className="qf-error" role="alert">{error}</p>}
            </aside>
            <aside className="qf-saved-card" aria-label="Saved searches">
              <div className="qf-preview-heading"><h2>Saved searches</h2></div>
              {!isAuthenticated ? (
                <p className="qf-saved-empty">
                  <button type="button" className="qf-link-button" onClick={() => {
                    saveAfterSignup.current = false;
                    setAccountModalOpen(true);
                  }}>Create a free account</button> to save searches and re-run them later.
                </p>
              ) : savedSearches.length === 0 ? (
                <p className="qf-saved-empty">Nothing saved yet. Build a search and choose Save Search.</p>
              ) : (
                <ul className="qf-saved-list">
                  {savedSearches.map(record => (
                    <li key={record.id}>
                      <div className="qf-saved-meta">
                        <span className="qf-saved-name">{record.name}</span>
                        <span className="qf-saved-tool">{record.tool === 'typo' ? 'Typo Hunter' : 'Advanced Builder'}</span>
                      </div>
                      <div className="qf-saved-actions">
                        <button type="button" disabled={rerunningId === record.id}
                          onClick={() => runSavedSearch(record)}>
                          {rerunningId === record.id ? 'Opening…' : 'Re-run'}
                        </button>
                        <button type="button" onClick={() => editSavedSearch(record)}>Edit</button>
                        <button type="button" className="qf-saved-delete"
                          onClick={() => removeSavedSearch(record.id)}
                          aria-label={`Delete ${record.name}`}>Delete</button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </aside>
          </div>
        </div>
        {tool === 'builder' && searchActions}
      </main>
      <CreateAccountModal
        open={accountModalOpen}
        onClose={() => { saveAfterSignup.current = false; setAccountModalOpen(false); }}
        onCreated={handleAccountCreated}
        onLogin={() => writeSearchDraft(tool, player, builder, removedVariants, typoFormat)}
        reason={saveAfterSignup.current
          ? 'Create a free account to save this search and re-run it any time.'
          : undefined}
      />
    </div>
  );
}
export default QuickFlipSearch;
